import * as React from 'react';

import ActionButton from 'components/buttons/action-button/ActionButton';
import DeliverableMediaContextMenu from '../../../media/parts/deliverable-media-context-menu/DeliverableMediaContextMenu';
import Input from 'components/inputs/input/Input';
import StatusLabel from 'components/status-label/StatusLabel';
import Popover from 'components/popover/Popover';

import DeliverableSubHeader from '../../deliverable-sub-header/DeliverableSubHeader';

import { APPROVAL_STATUSES, CONTENT_APPROVAL_STATUS_LABEL_MAP } from 'utils/contentApproval';
import {
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_TIKTOK_VIDEO,
  DELIVERABLE_TYPE_YOUTUBE_VIDEO } from 'utils/deliverables';
import { __t, applyTranslations } from 'utils/language';

import type { Deliverable, DeliverableAttachmentApprovalStatus, DeliverableWithContentApproval } from 'types';

import './deliverable-caption-approval.scss';

applyTranslations(require('./deliverable-caption-approval.i18n.json'));

type Props = {
  caption: string,
  isEditMode: boolean,
  isForBrand?: boolean,
  deliverable: DeliverableWithContentApproval,
  status: DeliverableAttachmentApprovalStatus,
  hasClientProvidedFeedback?: boolean,
  onSetCaption: (value: string) => void,
  onEditMode: (isEditMode: boolean) => void,
  onResetCaptionStatus: () => void,
  onCaptionStatusChange: (status: DeliverableAttachmentApprovalStatus) => void,
  onApplyClientFeedback: (deliverable: Deliverable, attachmentId?: number | null) => void
}

const MAX_CAPTION_LENGTH = {
  [DELIVERABLE_TYPE_INSTAGRAM_POST]: 2200,
  [DELIVERABLE_TYPE_INSTAGRAM_STORY]: 150,
  [DELIVERABLE_TYPE_TIKTOK_VIDEO]: 150,
  [DELIVERABLE_TYPE_YOUTUBE_VIDEO]: 5000
};

const msgKey = 'deliverable_caption_approval';

const DeliverableCaptionApproval = ({
  caption,
  deliverable,
  isEditMode,
  isForBrand,
  status,
  hasClientProvidedFeedback,
  onCaptionStatusChange,
  onResetCaptionStatus,
  onSetCaption,
  onApplyClientFeedback,
  onEditMode }: Props) => {
  const { caption: savedCaption } = deliverable.contentApproval;
  const isInputEditable = (isEditMode && (isForBrand || status !== APPROVAL_STATUSES.APPROVED) &&
    (isForBrand || !savedCaption.text || status === APPROVAL_STATUSES.DECLINED));
  const showActions = status === APPROVAL_STATUSES.PENDING && isForBrand && !isEditMode;
  const popoverText = __t(`${msgKey}.disabled_popover`);
  const tooltipMsg = !isForBrand && __t(`${msgKey}.tooltip`);
  const finalTooltipMsg = !isForBrand && (!isInputEditable ? popoverText : tooltipMsg);

  const renderApprovalActions = () => (
    <div className="deliverable-caption-approval__caption-approval">
      {
        !isInputEditable && (
          <Popover
            triggerComponent={(
              <div className="deliverable-caption-approval__caption-text">
                {caption}
              </div>
            )}
            classNameWrapper="deliverable-caption-approval__popover-wrapper"
          >
            <div className="deliverable-caption-approval__popover-contents">
              {popoverText}
            </div>
          </Popover>
        )
      }
      {
        isForBrand && !isEditMode && (
          <DeliverableMediaContextMenu
            key={`deliverable-caption-approval__context-menu--${deliverable.id}`}
            deliverable={deliverable}
            isEditMode={isEditMode}
            onUploadModeToggled={() => onEditMode(!isEditMode)}
            attachments={[]}
            width={32}
            captionStatus={status}
            hasClientAddedNewFeedback={hasClientProvidedFeedback}
            onResetCaptionApprovalStatus={onResetCaptionStatus}
            onApplyClientFeedback={onApplyClientFeedback}
          />
        )
      }
      {
        showActions && (
          <React.Fragment>
            <ActionButton
              color="tertiary"
              height="minimum"
              style="small"
              width="small"
              onClick={() => onCaptionStatusChange(APPROVAL_STATUSES.DECLINED)}
            >
              {__t(`${msgKey}.buttons.decline_caption`)}
            </ActionButton>
            <ActionButton
              color="primary"
              height="minimum"
              style="small"
              width="small"
              onClick={() => onCaptionStatusChange(APPROVAL_STATUSES.APPROVED)}
            >
              {__t(`${msgKey}.buttons.approve_caption`)}
            </ActionButton>
          </React.Fragment>
        )
      }
      {
        (!showActions || !isForBrand || (isEditMode && status === APPROVAL_STATUSES.DECLINED)) && (
          <div className="deliverable-caption-approval__status-label">
            <StatusLabel
              size="nano"
              type={CONTENT_APPROVAL_STATUS_LABEL_MAP[status]}
              label={__t(`${msgKey}.labels.${status}`)}
            />
          </div>
        )
      }
    </div>
  );

  return (
    <React.Fragment>
      <DeliverableSubHeader
        title={__t(`${msgKey}.labels.caption`)}
        instruction={isEditMode && isForBrand && __t(`${msgKey}.caption_instruction`)}
        directon={(isEditMode || caption) ? 'column' : 'row'}
        emptyNote={(isEditMode || caption) ? '' : __t(`${msgKey}.caption_empty_note`)}
        isCaptionHeader
        tooltipMsg={finalTooltipMsg}
      />
      <div className="deliverable-caption-approval__container">
        {
          isInputEditable && (
            <div className="deliverable-caption-approval__caption-input-container">
              <Input
                multiline
                width="responsive"
                name="approval-note"
                placeholder={__t(`${msgKey}.caption_placeholder`)}
                value={caption}
                onChange={(event) => onSetCaption(event?.target?.value)}
                charCount={{
                  curCount: caption.length,
                  maxChars: MAX_CAPTION_LENGTH[deliverable.deliverableType]
                }}
                className="deliverable-caption-approval__caption-input"
              />
            </div>
          )
        }
        {
          savedCaption.text && renderApprovalActions()
        }
      </div>
    </React.Fragment>
  );
};

export default DeliverableCaptionApproval;
